import React from "react";
import theme from "../../../../../styles/theme";
import drawSpaceConfig from "../config/drawSpaceConfig";
import DrawProperties from "./DrawProperties";

/*
====================================================================
HELPERS DE TERMINOLOGIE
====================================================================
*/

const getPanelTitle = (activeMode, entity) => {
  if (entity?.type === "OUVRAGE") return "Ouvrage Properties";
  if (entity?.type === "LEVEL") return "Level Properties";

  if (activeMode === "SITE") return "Ouvrage Properties";
  if (activeMode === "OUVRAGE") return "Level Properties";
  if (activeMode === "NIVEAU") return "Entity Properties";

  return drawSpaceConfig.labels.properties;
};

const getNameFieldLabel = (activeMode, entity) => {
  if (entity?.type === "OUVRAGE") return "Ouvrage";
  if (entity?.type === "LEVEL") return "Level";

  if (activeMode === "SITE") return "Ouvrage";
  if (activeMode === "OUVRAGE") return "Level";
  if (activeMode === "NIVEAU") return "Entity";

  return "Label";
};

/*
Type métier lisible
- structure : OUVRAGE / LEVEL
- niveau : ENTITY / EMPRISE
*/
const getReadableType = (entity, activeMode) => {
  if (!entity) return "";

  if (entity.type === "OUVRAGE") return "Ouvrage";
  if (entity.type === "LEVEL") return "Level";

  if (
    entity.type === drawSpaceConfig.entityTypes.AREA_RECT ||
    entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE ||
    entity.type === drawSpaceConfig.entityTypes.CIRCLE ||
    entity.type === drawSpaceConfig.entityTypes.LINE ||
    entity.type === drawSpaceConfig.entityTypes.ARC
  ) {
    return entity.kind === "EMPRISE" ? "Emprise" : "Entity";
  }

  if (activeMode === "SITE") return "Ouvrage";
  if (activeMode === "OUVRAGE") return "Level";
  if (activeMode === "NIVEAU") return "Entity";

  return entity.type ?? "";
};

/*
Type géométrique lisible
*/
const getReadableGeometryType = (entity) => {
  if (!entity) return "";

  if (entity.type === "OUVRAGE" || entity.type === "LEVEL") {
    if (entity.geometry?.points) return "Polyline";
    if (
      "x" in (entity.geometry ?? {}) &&
      "y" in (entity.geometry ?? {}) &&
      "width" in (entity.geometry ?? {}) &&
      "height" in (entity.geometry ?? {})
    ) {
      return "Rectangle";
    }
    return "";
  }

  if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) return "Rectangle";
  if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) return "Polyline";
  if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) return "Circle";
  if (entity.type === drawSpaceConfig.entityTypes.LINE) return "Line";
  if (entity.type === drawSpaceConfig.entityTypes.ARC) return "Arc";

  return "";
};

/*
Un level structurel peut avoir elevation au niveau racine,
une entity peut l’avoir dans attributes.
*/
const getElevationValue = (entity) => {
  if (!entity) return "";
  if (entity.elevation !== undefined && entity.elevation !== null) {
    return entity.elevation;
  }
  return entity.attributes?.elevation ?? "";
};

const toNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : parsed;
};

/*
Rattachement lisible
*/
const getReadableAttachment = (entity) => {
  if (!entity) return "";

  if (entity.type === "OUVRAGE") return "Site";
  if (entity.type === "LEVEL") return "Ouvrage";

  const parent = entity.parent ?? {};

  if (parent.levelId) return "Level";
  if (parent.ouvrageId) return "Ouvrage";
  if (parent.siteId) return "Site";

  return "";
};

/*
Surface calculée quand possible
- rectangle : width * height
- circle    : πr²
- polyline / line / arc : non calculé ici
*/
const getReadableSurface = (entity) => {
  if (!entity) return "";

  const geometry = entity.geometry ?? {};

  const hasRect =
    "width" in geometry &&
    "height" in geometry &&
    geometry.width !== "" &&
    geometry.height !== "";

  if (hasRect) {
    const width = Number(geometry.width);
    const height = Number(geometry.height);

    if (!Number.isNaN(width) && !Number.isNaN(height)) {
      return `${(width * height).toFixed(2)}`;
    }
  }

  if ("r" in geometry && geometry.r !== "") {
    const r = Number(geometry.r);

    if (!Number.isNaN(r)) {
      return `${(Math.PI * r * r).toFixed(2)}`;
    }
  }

  return "";
};

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================
*/

const DrawPropertiesPanel = ({
  entity,
  activeMode = "NIVEAU",
  onChange,
  systems = [],
  style = {},
}) => {
  const geometry = entity?.geometry ?? {};
  const attributes = entity?.attributes ?? {};

  const panelTitle = getPanelTitle(activeMode, entity);
  const nameFieldLabel = getNameFieldLabel(activeMode, entity);
  const readableType = getReadableType(entity, activeMode);
  const readableGeometryType = getReadableGeometryType(entity);
  const readableAttachment = getReadableAttachment(entity);
  const readableSurface = getReadableSurface(entity);

  const getSubsystemLabel = (subId) => {
    for (const system of systems) {
      const found = (system.subsystems ?? []).find((sub) => sub.id === subId);
      if (found) return found.label;
    }
    return subId;
  };

  if (!entity) {
    return (
      <div
        style={{
          width: 260,
          padding: theme.spacing.sm,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.sm,
          color: theme.colors.text.small,
          boxSizing: "border-box",
          ...style,
        }}
      >
        {drawSpaceConfig.labels.noSelection}
      </div>
    );
  }

  /*
  ================================================================
  HELPERS DE MISE À JOUR
  ================================================================
  */

  const updateField = (field, value) => {
    if (!onChange) return;

    onChange({
      ...entity,
      [field]: value,
    });
  };

  const updateGeometry = (field, value) => {
    if (!onChange) return;

    onChange({
      ...entity,
      geometry: {
        ...geometry,
        [field]: toNumberOrEmpty(value),
      },
    });
  };

  const updateAttributes = (field, value) => {
    if (!onChange) return;

    /*
    Pour LEVEL structurel, on peut porter elevation au niveau racine.
    Pour ENTITY, on garde attributes.elevation.
    */
    if (entity.type === "LEVEL" && field === "elevation") {
      onChange({
        ...entity,
        elevation: toNumberOrEmpty(value),
      });
      return;
    }

    onChange({
      ...entity,
      attributes: {
        ...attributes,
        [field]: toNumberOrEmpty(value),
      },
    });
  };

  const removeSubsystem = (subIdToRemove) => {
    if (!onChange) return;

    const next = (entity.subsystemIds ?? []).filter((id) => id !== subIdToRemove);

    onChange({
      ...entity,
      subsystemIds: next,
    });
  };

  return (
    <div
      style={{
        width: 260,
        minWidth: 20,
        padding: theme.spacing.sm,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        boxSizing: "border-box",
        marginTop: 85,
        marginLeft: -15,
        ...style,
      }}
    >
      <div
        style={{
          width: 260,
          fontFamily: theme.fonts.heading,
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.fontWeights.semiBold,
          color: theme.colors.text.primary,
          marginBottom: theme.spacing.xs,
        }}
      >
        {panelTitle}
      </div>

      <DrawProperties
        label="Type"
        value={readableType}
        readOnlyMode={true}
        labelWidth={90}
      />

      <DrawProperties
        label="Rattachement"
        value={readableAttachment}
        readOnlyMode={true}
        labelWidth={90}
      />

      <DrawProperties
        label={nameFieldLabel}
        value={entity.label ?? ""}
        onChange={(value) => updateField("label", value)}
        labelWidth={90}
        width={210}
      />

      {readableGeometryType ? (
        <DrawProperties
          label="Geometry"
          value={readableGeometryType}
          readOnlyMode={true}
          labelWidth={90}
        />
      ) : null}

      <DrawProperties
        label="Surface"
        value={readableSurface}
        readOnlyMode={true}
        labelWidth={90}
      />

      {"width" in geometry && (
        <DrawProperties
          label="Width"
          width={210}
          value={geometry.width ?? ""}
          onChange={(value) => updateGeometry("width", value)}
          labelWidth={90}
          type="number"
        />
      )}

      {"height" in geometry && (
        <DrawProperties
          label="Height"
          value={geometry.height ?? ""}
          onChange={(value) => updateGeometry("height", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"r" in geometry && (
        <DrawProperties
          label="Radius"
          value={geometry.r ?? ""}
          onChange={(value) => updateGeometry("r", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"cx" in geometry && (
        <DrawProperties
          label="Center X"
          value={geometry.cx ?? ""}
          onChange={(value) => updateGeometry("cx", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"cy" in geometry && (
        <DrawProperties
          label="Center Y"
          value={geometry.cy ?? ""}
          onChange={(value) => updateGeometry("cy", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"x1" in geometry && (
        <DrawProperties
          label="X1"
          value={geometry.x1 ?? ""}
          onChange={(value) => updateGeometry("x1", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"y1" in geometry && (
        <DrawProperties
          label="Y1"
          value={geometry.y1 ?? ""}
          onChange={(value) => updateGeometry("y1", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"x2" in geometry && (
        <DrawProperties
          label="X2"
          value={geometry.x2 ?? ""}
          onChange={(value) => updateGeometry("x2", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      {"y2" in geometry && (
        <DrawProperties
          label="Y2"
          value={geometry.y2 ?? ""}
          onChange={(value) => updateGeometry("y2", value)}
          labelWidth={70}
          width={210}
          type="number"
        />
      )}

      <DrawProperties
        label="Elevation"
        value={getElevationValue(entity)}
        onChange={(value) => updateAttributes("elevation", value)}
        labelWidth={70}
        width={210}
        type="number"
      />

      {entity.kind ? (
        <DrawProperties
          label="Kind"
          value={entity.kind}
          readOnlyMode={true}
          labelWidth={70}
        />
      ) : null}

      {Array.isArray(entity?.subsystemIds) && entity.subsystemIds.length > 0 && (
        <div
          style={{
            width: 260,
            marginTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSizes.xs,
              fontWeight: theme.fontWeights.regular,
              color: theme.colors.text.small,
            }}
          >
            Subsystems
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {entity.subsystemIds.map((subId) => (
              <div
                key={subId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  borderRadius: 999,
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fontSizes.xs,
                  color: theme.colors.text.body,
                  background: theme.colors.background.panel,
                  border: `1px solid ${theme.colors.borders.default}`,
                  lineHeight: 1.4,
                }}
              >
                <span>{getSubsystemLabel(subId)}</span>

                <span
                  onClick={() => removeSubsystem(subId)}
                  style={{
                    cursor: "pointer",
                    fontSize: 10,
                    opacity: 0.6,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = 1;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = 0.6;
                  }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawPropertiesPanel;